import { Component, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { interval, Subscription } from 'rxjs';
import { ChatPreviewDialogComponent } from '../../components/dialogs/chat-preview-dialog/chat-preview-dialog.component';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { EmbeddingDialogComponent } from '../../components/dialogs/embedding-dialog/embedding-dialog.component';
import { TutorDialogComponent } from '../../components/dialogs/tutor-dialog/tutor-dialog.component';
import { TutorWizardComponent } from '../../components/dialogs/tutor-wizard/tutor-wizard.component';
import { AccessService } from '../../services/access.service';
import { AuthService, UserRoles } from '../../services/auth.service';
import { ChatPreviewService } from '../../services/chat-preview.service';
import { ChatService } from '../../services/chat.service';
import { DataSourcesService } from '../../services/data-sources.service';
import { ScriptService } from '../../services/script.service';
import { Tutor, TutorService, TutorTemplate } from '../../services/tutor.service';
import { UserService } from '../../services/user.service';
import { UsageService } from '../../services/usage.service';
import { AgentRefreshService } from '../../agent/agent-refresh.service';

@Component({
  selector: 'app-tutors',
  templateUrl: './tutors.component.html',
  styleUrl: './tutors.component.scss',
})
export class TutorsComponent implements OnDestroy {
  subscriptions: Subscription[] = [];

  isLoading = true;

  tutors: Tutor[] = this.tutorService.tutors$.value;

  searchText: string = '';

  /** Active template-name filter. `'all'` shows every group. */
  selectedTemplateFilter: string = 'all';

  /** Active "filter by user access" selection. `'all'` shows every agent; a
   *  user id shows only the agents that user may see (own + granted). */
  selectedUserFilter: string = 'all';

  /** Bot ids the currently-selected user may access, or null while none is
   *  selected / still loading. Filled from GET /access/bot/{userId} so the
   *  filter reflects real access, not authorship. */
  private accessibleBotIds: Set<string> | null = null;

  /** All client users, for the filter dropdown. Loaded once for admin/manager. */
  users: { id: string; name: string }[] = [];

  /**
   * The user filter is only for roles that see the whole client. Everyone else
   * is already scoped to their own + granted agents by the backend, so the
   * control would only let them hide rows — and with it hidden they must never
   * start out pre-filtered, or granted agents would vanish with no way back.
   */
  get canFilterByUser(): boolean {
    const role = this.authService.accessTokenPayload$.value?.role;
    return (
      role === UserRoles.Admin ||
      role === UserRoles.SuperAdmin ||
      role === UserRoles.Manager
    );
  }

  async onUserFilterChange(userId: string): Promise<void> {
    this.selectedUserFilter = userId;
    if (userId === 'all') {
      this.accessibleBotIds = null;
      return;
    }
    try {
      this.accessibleBotIds = new Set(
        await this.accessService.getUserBotIds(userId),
      );
    } catch {
      // On failure fall back to showing nothing for that user rather than
      // silently showing everything, which would misrepresent their access.
      this.accessibleBotIds = new Set();
    }
  }

  cardSize: 'small' | 'large' =
    (localStorage.getItem('al-tutorCardSize') as any) || 'large';

  /**
   * Templates that the client actually has tutors for, derived from
   * `tutorTemplates$ ∩ distinct(tutors$.template_id)`. Filter dropdown
   * options + the section list both read from this getter so the
   * overview only shows what's in use.
   */
  get availableTemplates(): TutorTemplate[] {
    const templates = this.tutorService.tutorTemplates$.value;
    const usedIds = new Set(
      this.tutorService.tutors$.value
        .map((t) => t.template_id)
        .filter((id): id is string => !!id)
    );
    return templates.filter((t) => usedIds.has(t.id));
  }

  /** Load the client user list for the filter dropdown. Called once for
   *  admin/manager; silently no-ops for everyone else (the control is hidden). */
  private loadUsers(): void {
    if (!this.canFilterByUser) return;
    this.userService.listUsers().subscribe((list) => {
      this.users = list
        .map((u) => ({ id: u.id, name: u.name || u.email }))
        .sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  /**
   * Tutors after applying the search text, the template filter and the
   * per-user access filter. Grouping in the template iterates over templates
   * and reads from this list to build per-template buckets.
   */
  get filteredTutors(): Tutor[] {
    const q = (this.searchText || '').toLowerCase().trim();
    return this.tutorService.tutors$.value.filter((t) => {
      if (
        this.selectedTemplateFilter !== 'all' &&
        t.template_id !== this.selectedTemplateFilter
      ) {
        return false;
      }
      if (this.accessibleBotIds && !this.accessibleBotIds.has(t.id)) {
        return false;
      }
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        (t.visual_name?.toLowerCase().includes(q) ?? false) ||
        (t.description?.toLowerCase().includes(q) ?? false)
      );
    });
  }

  /** Stable trackBy for the *ngFor over tutor cards inside each section. */
  trackTutor = (_: number, t: Tutor) => t.id;

  /** Stable trackBy for the derived template sections. The getter below
   * returns new wrapper objects on each change detection run; without a
   * stable key Angular recreates every section and all tutor-card images. */
  trackTutorSection = (
    _: number,
    section: { template: TutorTemplate; tutors: Tutor[] }
  ) => section.template.id;

  /** Sections rendered on the page — one per template that actually has
   *  tutors after filtering. */
  get tutorSections(): { template: TutorTemplate; tutors: Tutor[] }[] {
    const filtered = this.filteredTutors;
    return this.availableTemplates
      .map((template) => ({
        template,
        tutors: filtered.filter((t) => t.template_id === template.id),
      }))
      .filter((s) => s.tutors.length > 0);
  }

  constructor(
    protected tutorService: TutorService,
    protected dialogService: NbDialogService,
    protected usageService: UsageService,
    protected chatService: ChatService,
    protected chatPreviewService: ChatPreviewService,
    protected scriptService: ScriptService,
    protected dataSourcesService: DataSourcesService,
    protected authService: AuthService,
    protected accessService: AccessService,
    protected userService: UserService,
    protected router: Router
  ) {
    this.getConnections();
    this.loadUsers();
    this.tutorService.listTutorTemplates().subscribe((c) => {});

    this.tutorService.listTutors().subscribe(() => {
      this.isLoading = false;
    });

    this.tutorService.tutors$.subscribe((tutors) => {
      this.tutors = tutors;
    });

    inject(AgentRefreshService)
      .on('bots')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.tutorService.listTutors().subscribe());
  }

  onSearchChange() {
    setTimeout(() => {
      this.tutors = this.tutorService.tutors$.value.filter(
        (tutor) =>
          tutor.name.toLowerCase().includes(this.searchText.toLowerCase()) ||
          tutor.visual_name
            ?.toLowerCase()
            .includes(this.searchText.toLowerCase()) ||
          tutor.description
            ?.toLowerCase()
            .includes(this.searchText.toLowerCase())
      );
    }, 10);
  }

  onCardSizeSelect(event: any) {
    const size = event[0];
    this.cardSize = size;
    localStorage.setItem('al-tutorCardSize', size);
  }

  getConnections() {
    this.tutorService.listConnections().subscribe();

    this.subscriptions.push(
      interval(10000).subscribe(() => {
        this.tutorService.listConnections().subscribe();
      })
    );
  }

  onStartChat() {
    this.dialogService.open(ChatPreviewDialogComponent, {});
  }

  onCloseChat() {
    this.chatPreviewService.closeChat();
  }

  onEmbedChat() {
    this.dialogService.open(EmbeddingDialogComponent, {
      context: {
        botId: undefined,
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onAddTutor() {
    this.dialogService.open(TutorDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        data: undefined,
        onComplete: (tutor) => {
          this.tutorService.listTutors().subscribe(() => {});
          this.maybeOfferWizard(tutor);
        },
      },
    });
  }

  /**
   * If the freshly created tutor uses the alpha_flexible template, offer
   * the guided wizard. The user can decline and configure freely on the
   * AI-Konfig page later.
   */
  private maybeOfferWizard(tutor: Tutor) {
    if (!tutor?.id || !tutor.template_id) return;
    const template = this.tutorService.tutorTemplates$.value.find(
      (t) => t.id === tutor.template_id
    );
    if (!template || template.name !== 'alpha_flexible') return;

    const ref = this.dialogService.open(ConfirmDialogComponent, {
      hasScroll: false,
      closeOnBackdropClick: false,
      context: {
        title: 'tutor.wizard.offer.title',
        message: 'tutor.wizard.offer.message',
      },
    });
    ref.onClose.subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.openWizardForTutor(tutor);
      }
    });
  }

  private openWizardForTutor(tutor: Tutor) {
    this.dialogService.open(TutorWizardComponent, {
      hasScroll: true,
      closeOnBackdropClick: false,
      context: {
        existingTutor: tutor,
        onComplete: () => {
          this.tutorService.listTutors().subscribe(() => {});
        },
      },
    });
  }

  onEditTutor(tutor: Tutor) {
    const dialogRef = this.dialogService.open(TutorDialogComponent, {
      hasScroll: true,
      closeOnBackdropClick: true,
      context: {
        data: tutor,
      },
    });
  }

  get sources() {
    return this.dataSourcesService.dataSources$.value;
  }

  getActiveUsers(tutorId: string): number {
    const connections = this.tutorService.connections$?.value?.find(
      (c) => c.tutorId === tutorId
    );
    return connections?.active_sessions ?? 0;
  }
}
